import React, { useEffect, useState, useCallback } from 'react';

import { Grid } from '@material-ui/core';
import { useMergeLink } from '@mergeapi/react-merge-link';
import { useFinchConnect } from '@tryfinch/react-connect';
import { HeaderMarginContainer } from 'Deer/components/HeaderComponents/HeaderMarginContainer';
import { useToast } from 'components/hooks/useToast';
import { useDispatch } from 'react-redux';
import { Button } from 'styles/admin';
import { checkMergeIntegrationEnabled } from 'utils/helpers/featureFlags';

import { FINCH_CLIENT_ID, FINCH_CLIENT_ID_PROD, TOAST_STATUSES } from 'constants/ProjectConstants';
import { HrisLogoSrc } from 'constants/icons';
import { sendPageViewEvent } from 'constants/mixpanelHelper';

import {
  storePermanentAccountToken,
  getLinkToken,
  getMergeHRISAuthDetails,
  storePermanentAccountTokenFinch,
  getFinchHRISAuthDetails,
} from 'services/plugins';
import { showErrorSnackbar } from 'services/snackbar';

import { Typography16, Typography17 } from 'uiToolkit/Typography';

import InstalledButton from '../InstalledButton';
import { StyledContainer, Logo } from '../styles';
import UninstallSection from './UninstallSection';
import GuideSection from './guideSection';
import InstallationSection from './installationSection';

export const PLUGIN_TYPE = { FINCH: 'FINCH', MERGE: 'MERGE' };

function MergeHRISIntegration({ isActivated, onActivationSuccess }) {
  const [linkToken, setLinkToken] = useState(null);
  const [isFetchingLinkToken, setIsFetchingLinkToken] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    setIsFetchingLinkToken(true);
    const pluginType = !checkMergeIntegrationEnabled() ? PLUGIN_TYPE.MERGE : PLUGIN_TYPE.FINCH;

    getLinkToken(pluginType)
      .then((public_token) => setLinkToken(public_token))
      .catch(() => dispatch(showErrorSnackbar('This action failed. Please refresh your page and try again')))
      .finally(() => setIsFetchingLinkToken(false));
  }, []);

  return !linkToken ? (
    <MergeHRISPlaceholderButton loading={isFetchingLinkToken} buttonText="Install" />
  ) : (
    <HRISButton linkToken={linkToken} isActivated={isActivated} onActivationSuccess={onActivationSuccess} />
  );
}

function FinchHRISIntegration({ isFinchActivated, setIsFinchActivated }) {
  const toast = useToast();

  const onSuccess = async ({ code }) => {
    try {
      await storePermanentAccountTokenFinch({ public_token: code });
      setIsFinchActivated(true);
    } catch (error) {
      console.error('error storing finch token', error);
      toast({
        title: error.message,
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
    }
  };
  const onError = ({ errorMessage }) => {
    console.error('error fetching finch token', errorMessage);
  };

  const { open } = useFinchConnect({
    clientId: process.env.NODE_ENV === 'production' ? FINCH_CLIENT_ID_PROD : FINCH_CLIENT_ID,
    products: ['company', 'directory', 'individual', 'employment'],
    sandbox: false,
    manual: true,
    onSuccess,
    onError,
  });

  if (isFinchActivated || isFinchActivated === null) {
    return <InstalledButton isDisabled>Installed</InstalledButton>;
  }

  return (
    <Button blue size="large" onClick={open} style={{ width: '160px', paddingLeft: 0, paddingRight: 0 }}>
      <text> Install</text>
    </Button>
  );
}

function MergeHRISPlaceholderButton({ loading, buttonText }) {
  return (
    <InstalledButton isDisabled isLoading={loading}>
      {buttonText || 'Installed'}
    </InstalledButton>
  );
}

// this hook useMergeLink must be called with linkToken at first render only
// that's why decoupled to another component and only called conditionally
const HRISButton = ({ linkToken, isActivated, onActivationSuccess }) => {
  const onSuccess = useCallback(
    (public_token) => {
      storePermanentAccountToken({ public_token });
      onActivationSuccess();
    },
    [onActivationSuccess]
  );

  const { open, isReady } = useMergeLink({
    linkToken,
    onSuccess,
  });

  return (
    <Button
      blue
      size="large"
      // topGutter={4}
      onClick={open}
      disabled={!isReady}
      style={{ width: '160px', paddingLeft: 0, paddingRight: 0 }}
    >
      {isActivated ? 'View Integration' : 'Install'}
    </Button>
  );
};

/**
 * @deprecated 8-04-2025
 */
const HRISPlugin = () => {
  const [isActivated, setIsActivated] = useState(null);
  const [isFinchActivated, setIsFinchActivated] = useState(null);
  const [loading, setLoading] = useState(false);

  const checkAuthenticationStatus = () => {
    setLoading(true);
    if (checkMergeIntegrationEnabled()) {
      getMergeHRISAuthDetails()
        .then(() => {
          setIsActivated(true);
        })
        .catch((err) => {
          // dispatch(showErrorSnackbar(err?.message || ''));
          setIsActivated(false);
        })
        .finally(() => setLoading(false));
    } else {
      getFinchHRISAuthDetails()
        .then(() => {
          setIsFinchActivated(true);
        })
        .catch((error) => {
          // dispatch(showErrorSnackbar(err?.message || ''));
          setIsFinchActivated(false);
        });
    }
  };

  useEffect(checkAuthenticationStatus, []);

  useEffect(() => {
    sendPageViewEvent({
      eventProperties: {
        source: 'Company-settings-HRIS',
      },
    });
  }, []);

  const onActivationSuccessHandler = useCallback(() => setIsActivated(true), []);
  return (
    <StyledContainer>
      <HeaderMarginContainer>
        <Grid container spacing={4}>
          <Grid item>
            <Logo src={HrisLogoSrc} width={80} />
          </Grid>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <Grid item xs={6}>
            <Grid item xs container direction="column" spacing={2}>
              <Grid item xs>
                <Typography16 bottomGutter={2}>HRIS Plugin</Typography16>
                <Typography17 bottomGutter={3}>
                  Manage your HR workflow from Mesh, and watch job satisfaction
                  <br />
                  and engagement go through the roof!
                </Typography17>
              </Grid>
              <Grid item>
                {checkMergeIntegrationEnabled() ? (
                  <>
                    {(isActivated === null || isActivated === true) && (
                      <MergeHRISPlaceholderButton setIsActivated={setIsActivated} loading={loading} />
                    )}
                    {isActivated === false && (
                      <MergeHRISIntegration
                        isActivated={isActivated}
                        onActivationSuccess={onActivationSuccessHandler}
                      />
                    )}
                  </>
                ) : (
                  <FinchHRISIntegration setIsFinchActivated={setIsFinchActivated} isFinchActivated={isFinchActivated} />
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </HeaderMarginContainer>

      {(isActivated === false || isFinchActivated === false) && <InstallationSection />}
      {(isActivated === true || isFinchActivated === true) && <UninstallSection />}
      <GuideSection />
    </StyledContainer>
  );
};

export default HRISPlugin;
